'use client';

import { useAppSelector } from '@/state';
import { ChangeOrdersApi } from '@/state/api';
import {
  Badge,
  Button,
  Center,
  Input as ChakraInput,
  Flex,
  NativeSelect,
  Separator,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Input as AppInput, Card, OverlayDialog, toasterApi } from '@ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useRef, useState } from 'react';

dayjs.extend(utc);

const EMPTY_ROWS: any[] = [];

// -----------------------------
// utils
// -----------------------------

const statusBadge = (status?: string) => {
  const s = (status || '').toLowerCase();
  if (s === 'approved') return { colorPalette: 'green', label: 'Approved' };
  if (s === 'submitted') return { colorPalette: 'blue', label: 'Submitted' };
  if (s === 'rejected') return { colorPalette: 'red', label: 'Rejected' };
  if (s === 'converted') return { colorPalette: 'purple', label: 'Converted' };
  if (s === 'rom') return { colorPalette: 'orange', label: 'ROM' };
  return { colorPalette: 'gray', label: status || 'Draft' };
};

const cleanMoney = (v: string) => {
  const s = (v || '').trim();
  if (!s) return null;
  const cleaned = s.replace(/[$, ]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  return cleaned;
};

const fmtMoneyAmount = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(String(value).replace(/[$, ]/g, ''));
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const fmtDate = (v: any) => {
  const d = dayjs(v);
  return d.isValid() ? d.format('MM/DD/YY') : '—';
};

const toDateInputValue = (v: any) => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

// store picked date as noon UTC to avoid timezone/off-by-one issues
const submittedDateToIso = (dateStr: string) => {
  const s = String(dateStr || '').trim();
  if (!s) return null;
  const d = dayjs.utc(`${s}T12:00:00Z`);
  return d.isValid() ? d.toISOString() : null;
};

const fmtSubmittedCell = (r: any) => {
  if (!r) return '—';

  const submittedAt = r?.submitted_at;
  if (dayjs(submittedAt).isValid()) return fmtDate(submittedAt);

  const note = String(r?.submitted_note || '').trim();
  if (note) return note;

  if (String(r?.status || '').toLowerCase() === 'rom') return 'ROM';
  return '—';
};

const normalizePatch = (patch: any) => {
  const out: any = { ...patch };
  if ('submitted_amount' in out)
    out.submitted_amount = cleanMoney(String(out.submitted_amount || ''));
  if ('approved_amount' in out)
    out.approved_amount = cleanMoney(String(out.approved_amount || ''));
  if ('co_number' in out)
    out.co_number = String(out.co_number || '').trim() || null;
  if ('title' in out) out.title = String(out.title || '').trim();
  if ('submitted_note' in out)
    out.submitted_note = String(out.submitted_note || '').trim() || null;
  if ('included_in_contract' in out)
    out.included_in_contract = !!out.included_in_contract;
  return out;
};

// frontend-only CP filter helper
const normalizeCp = (v: any) =>
  String(v || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/-/g, '');

// row highlight (kept)
const rowHighlightProps = (row: any) => {
  const s = String(row?.status || '').toLowerCase();
  const note = String(row?.submitted_note || '').trim();

  const isRom = s === 'rom' || /^rom\b/i.test(note);
  const isSubmitted = s === 'submitted' && dayjs(row?.submitted_at).isValid();
  const isApproved = s === 'approved';
  const isRejected = s === 'rejected';

  if (isRejected) return { bg: { base: 'red.50', _dark: 'red.950' } };
  if (isApproved) return { bg: { base: 'green.50', _dark: 'green.950' } };
  if (isSubmitted) return { bg: { base: 'blue.50', _dark: 'blue.950' } };
  if (isRom) return { bg: { base: 'orange.50', _dark: 'orange.950' } };
  return {};
};

const makeSelectHandler =
  (rowId: number, selectRow: (id: number) => void) => (e: any) => {
    const el = e?.target as HTMLElement | null;
    if (!el) return;

    // ignore clicks inside interactive controls
    if (el.closest('button, a, input, textarea, select, [role="button"]'))
      return;

    selectRow(rowId);
  };

// -----------------------------
// page
// -----------------------------

const ChangeOrdersIndex = () => {
  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const projectId = selectedProject?.id as number | undefined;

  // Project-derived (backend truth)
  const jobNo =
    String(
      (selectedProject as any)?.project_code ??
        (selectedProject as any)?.job_number ??
        (selectedProject as any)?.job_no ??
        '',
    ) || '';
  const jobName =
    String(
      (selectedProject as any)?.name ??
        (selectedProject as any)?.job_name ??
        '',
    ) || '';
  const contractor = String((selectedProject as any)?.contractor ?? '') || '';

  // -----------------------------
  // Sheet settings (one per project): sheet_title + base_lump_sum
  // -----------------------------
  const {
    data: sheetSettingsData,
    refetch: refetchSheetSettings,
    isFetching: isFetchingSheetSettings,
  } = ChangeOrdersApi.Api.useGetPendingChangeSheetSettingsQuery(
    { query: { project: projectId as number } } as any,
    { skip: !projectId },
  );

  const [patchSheetSettings] =
    ChangeOrdersApi.Api.usePatchPendingChangeSheetSettingsMutation();

  const [sheetTitle, setSheetTitle] = useState<string>(
    'KARAS & KARAS PENDING CHANGE ORDERS TRACKING SHEET',
  );
  const [contractValue, setContractValue] = useState<string>('');

  useEffect(() => {
    if (!projectId) return;
    const s = sheetSettingsData?.data;
    setSheetTitle(
      String(
        s?.sheet_title || 'KARAS & KARAS PENDING CHANGE ORDERS TRACKING SHEET',
      ),
    );
    setContractValue(
      String(
        s?.contract_value ??
          (selectedProject as any)?.contract_value ??
          (selectedProject as any)?.base_lump_sum ??
          '',
      ) || '',
    );
  }, [projectId, sheetSettingsData?.data?.updated_at]);

  const refreshSheetSettings = async () => {
    if (!projectId) return;
    try {
      await (refetchSheetSettings() as any).unwrap?.();
    } catch {
      // non-fatal
    }
  };

  const SheetSettingsForm = ({
    initialTitle,
    initialBase,
  }: {
    initialTitle: string;
    initialBase: string;
  }) => {
    const [draftTitle, setDraftTitle] = useState<string>(initialTitle);
    const [draftBase, setDraftBase] = useState<string>(initialBase);
    const [saving, setSaving] = useState(false);

    return (
      <Flex flexDirection="column" gap={4} width="full">
        <VStack align="stretch" gap={2}>
          <Text fontWeight="bold">Sheet Title</Text>
          <ChakraInput
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
          />
          <Text fontSize="xs" opacity={0.7}>
            Used as the PDF title/header and file name.
          </Text>
        </VStack>

        <VStack align="stretch" gap={2}>
          <Text fontWeight="bold">Contract Value</Text>

          <ChakraInput
            value={draftBase}
            onChange={(e) => setDraftBase(e.target.value)}
            placeholder="100000000"
          />
          <Text fontSize="xs" opacity={0.7}>
            Fills the template header.
          </Text>
        </VStack>

        <Separator />

        <VStack align="stretch" gap={1}>
          <Text fontWeight="bold">Job (from Project)</Text>
          <Text fontSize="sm" opacity={0.8}>
            Job No: <b>{jobNo || '—'}</b>
          </Text>
          <Text fontSize="sm" opacity={0.8}>
            Job Name: <b>{jobName || '—'}</b>
          </Text>
          <Text fontSize="sm" opacity={0.8}>
            Contractor: <b>{contractor || '—'}</b>
          </Text>
        </VStack>

        <Flex justifyContent="flex-end" gap={2}>
          <Button
            variant="outline"
            onClick={() => OverlayDialog.close('pendingChangeSheetSettings')}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            disabled={isFetchingSheetSettings || saving}
            onClick={async () => {
              if (!projectId) return;
              setSaving(true);
              try {
                await patchSheetSettings({
                  query: { project: projectId as number },
                  body: {
                    sheet_title: draftTitle,
                    contract_value: cleanMoney(String(draftBase || '')) || null,
                  },
                } as any).unwrap();

                setSheetTitle(draftTitle);
                setContractValue(draftBase);

                await refreshSheetSettings();
                OverlayDialog.close('pendingChangeSheetSettings');
                toasterApi({
                  type: 'success',
                  status: 200,
                  code: 'Saved',
                  message: 'Sheet settings saved',
                });
              } catch (e: any) {
                toasterApi({
                  type: 'error',
                  status: 500,
                  code: 'Error',
                  message: e?.data?.detail || 'Failed to save sheet settings',
                });
              } finally {
                setSaving(false);
              }
            }}
          >
            Save Settings
          </Button>
        </Flex>
      </Flex>
    );
  };

  const openSheetSettings = () => {
    if (!projectId) return;
    OverlayDialog.open('pendingChangeSheetSettings', {
      overlayId: 'pendingChangeSheetSettings',
      title: 'Sheet Settings',
      content: (
        <SheetSettingsForm
          initialTitle={sheetTitle}
          initialBase={contractValue}
        />
      ),
    });
  };

  // -----------------------------
  // CP# FILTER (frontend only)
  // -----------------------------
  const [searchTerm, setSearchTerm] = useState<string>('');
  const handleSearchChange = (v: any) => {
    const next = typeof v === 'string' ? v : (v?.target?.value ?? '');
    setSearchTerm(next);
  };

  // Section filter (backend-driven)
  const [section, setSection] = useState<'pending' | 'all'>('pending');

  // -----------------------------
  // List
  // -----------------------------
  const {
    data: listData,
    refetch: refetchList,
    isFetching: isFetchingList,
    isLoading: isLoadingList,
  } = ChangeOrdersApi.Api.useGetPendingChangesQuery(
    {
      query: {
        project: projectId as number,
        section,
      },
    } as any,
    { skip: !projectId },
  );

  const rawRows = listData?.data ?? EMPTY_ROWS;

  const allById = useMemo(() => {
    const m = new Map<number, any>();
    (rawRows ?? []).forEach((r: any) => m.set(r.id, r));
    return m;
  }, [rawRows]);

  const listRows = useMemo(() => {
    const q = normalizeCp(searchTerm);
    if (!q) return rawRows;

    return rawRows.filter((r: any) => {
      const a = normalizeCp(r.cp_display);
      const b = normalizeCp(r.cp_number);
      return a.includes(q) || b.includes(q);
    });
  }, [rawRows, searchTerm]);

  // -----------------------------
  // Dirty patches
  // -----------------------------
  const dirtyRef = useRef<Record<number, any>>({});
  const [dirtyCount, setDirtyCount] = useState<number>(0);
  const [saveState, setSaveState] = useState<'saved' | 'dirty' | 'saving'>(
    'saved',
  );
  const [dirtyTick, setDirtyTick] = useState<number>(0);

  const updateDirtyMeta = () => {
    const c = Object.keys(dirtyRef.current).length;
    setDirtyCount(c);
    setSaveState(c ? 'dirty' : 'saved');
  };

  const [activeId, setActiveId] = useState<number | null>(null);
  const activeRow = activeId ? allById.get(activeId) : rawRows[0];

  const [local, setLocal] = useState<any>(null);

  const queuePatch = (id: number, patch: any) => {
    dirtyRef.current[id] = { ...(dirtyRef.current[id] || {}), ...patch };
    updateDirtyMeta();
    setDirtyTick((t) => t + 1);
  };

  const getRowDraft = (r: any) => {
    const patch = dirtyRef.current[r.id] || {};
    return {
      title: patch.title ?? r.title ?? '',
      submitted_note: patch.submitted_note ?? r.submitted_note ?? '',
      submitted_at: patch.submitted_at ?? r.submitted_at ?? null,
      co_number: patch.co_number ?? r.co_number ?? '',
      submitted_amount: patch.submitted_amount ?? r.submitted_amount ?? '',
      approved_amount: patch.approved_amount ?? r.approved_amount ?? '',
      included_in_contract:
        patch.included_in_contract ?? !!r.included_in_contract,
      status: patch.status ?? r.status ?? 'draft',
    };
  };

  // -----------------------------
  // Mutations
  // -----------------------------
  const [postMutation] = ChangeOrdersApi.Api.usePostPendingChangeMutation();
  const [deleteMutation] = ChangeOrdersApi.Api.useDeletePendingChangeMutation();
  const [bulkUpdate] =
    ChangeOrdersApi.Api.usePostPendingChangeBulkUpdateMutation();

  const saveRow = async (id: number) => {
    const patch = dirtyRef.current[id];
    if (!patch) return true;

    const updates = [{ id, patch: normalizePatch(patch) }];

    try {
      setSaveState('saving');
      await bulkUpdate({ body: { updates } } as any).unwrap();

      const next = { ...dirtyRef.current };
      delete next[id];
      dirtyRef.current = next;

      await refetchList();
      updateDirtyMeta();
      setDirtyTick((t) => t + 1);
      setSaveState(Object.keys(dirtyRef.current).length ? 'dirty' : 'saved');
      return true;
    } catch (e: any) {
      setSaveState('dirty');
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message: e?.data?.detail || 'Failed to save row',
      });
      return false;
    }
  };

  useEffect(() => {
    if (!rawRows.length) {
      setActiveId(null);
      setLocal(null);
      dirtyRef.current = {};
      updateDirtyMeta();
      setDirtyTick((t) => t + 1);
      return;
    }
    if (!activeId || !allById.has(activeId)) setActiveId(rawRows[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawRows, allById]);

  useEffect(() => {
    if (!activeRow) return;
    const patch = dirtyRef.current[activeRow.id] || {};
    setLocal({
      title: patch.title ?? activeRow.title ?? '',
      submitted_note: patch.submitted_note ?? activeRow.submitted_note ?? '',
      status: patch.status ?? activeRow.status ?? 'draft',
      submitted_at: patch.submitted_at ?? activeRow.submitted_at ?? null,
      co_number: patch.co_number ?? activeRow.co_number ?? '',
      submitted_amount:
        patch.submitted_amount ?? activeRow.submitted_amount ?? '',
      approved_amount: patch.approved_amount ?? activeRow.approved_amount ?? '',
      included_in_contract:
        patch.included_in_contract ?? !!activeRow.included_in_contract,
    });
  }, [activeRow?.id, dirtyTick]);

  const selectRow = async (id: number) => {
    if (id === activeId) return;
    const hasDirty = !!(activeId && dirtyRef.current[activeId]);
    if (hasDirty) {
      const ok = await saveRow(activeId!);
      if (!ok) return;
    }
    setActiveId(id);
  };

  const handleAddRow = async () => {
    if (!projectId) return;
    try {
      if (activeId && dirtyRef.current[activeId]) {
        const ok = await saveRow(activeId);
        if (!ok) return;
      }

      const created = await postMutation({
        body: {
          project: projectId,
          title: 'New Pending Change',
          status: 'draft',
        },
      } as any).unwrap();

      await refetchList();
      const newId = created?.data?.id;
      if (newId) setActiveId(newId);

      toasterApi({
        type: 'success',
        status: 201,
        code: 'Success',
        message: 'Added',
      });
    } catch (e: any) {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message: e?.data?.detail || 'Failed to add',
      });
    }
  };

  const confirmDeleteRow = (id: number) => {
    OverlayDialog.open(`confirmDeletePendingChange_${id}`, {
      overlayId: `confirmDeletePendingChange_${id}`,
      title: 'Delete Pending Change?',
      content: (
        <Flex flexDirection="column" gap={4} width="full">
          <Text>This will permanently delete this item.</Text>
          <Flex justifyContent="flex-end" gap={2}>
            <Button
              variant="outline"
              onClick={() =>
                OverlayDialog.close(`confirmDeletePendingChange_${id}`)
              }
            >
              Cancel
            </Button>
            <Button
              colorPalette="red"
              onClick={async () => {
                try {
                  if (dirtyRef.current[id]) {
                    const ok = await saveRow(id);
                    if (!ok) return;
                  }

                  await deleteMutation({
                    params: { pending_change_id: id },
                  } as any).unwrap();

                  const next = { ...dirtyRef.current };
                  delete next[id];
                  dirtyRef.current = next;
                  updateDirtyMeta();
                  setDirtyTick((t) => t + 1);

                  if (activeId === id) {
                    setActiveId(null);
                    setLocal(null);
                  }

                  OverlayDialog.close(`confirmDeletePendingChange_${id}`);
                  await refetchList();
                  toasterApi({
                    type: 'success',
                    status: 204,
                    code: 'Deleted',
                    message: 'Deleted',
                  });
                } catch (e: any) {
                  toasterApi({
                    type: 'error',
                    status: 500,
                    code: 'Error',
                    message: e?.data?.detail || 'Failed to delete',
                  });
                }
              }}
            >
              Delete
            </Button>
          </Flex>
        </Flex>
      ),
    });
  };

  const [exportSheetMutation] =
    ChangeOrdersApi.Api.usePostPendingChangeExportPdfMutation();
  const [isExporting, setIsExporting] = useState(false);
  const handleExport = async () => {
    if (!projectId) return;

    if (activeId && dirtyRef.current[activeId]) {
      const ok = await saveRow(activeId);
      if (!ok) return;
    }

    try {
      setIsExporting(true);
      const blob = await exportSheetMutation({
        query: { project: projectId },
      } as any).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(sheetTitle || 'pending_changes').replaceAll(' ', '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toasterApi({
        type: 'success',
        status: 200,
        code: 'Success',
        message: 'Exported',
      });
    } catch (e: any) {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Error',
        message: e?.data?.detail || 'Failed to export',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const markStatus = async (next: string) => {
    const targetId = activeId ?? activeRow?.id;
    if (!targetId) return;

    const draft = { ...(local || {}), ...(dirtyRef.current[targetId] || {}) };
    const submittedAmtClean = cleanMoney(String(draft.submitted_amount || ''));
    const approvedAmtClean = cleanMoney(String(draft.approved_amount || ''));
    const co = String(draft.co_number || '').trim();
    const note = String(draft.submitted_note || '').trim();

    if (next === 'submitted' && !submittedAmtClean) {
      toasterApi({
        type: 'error',
        status: 400,
        code: 'Missing Field',
        message: 'Enter a valid Submitted $ before marking Submitted.',
      });
      return;
    }

    if (next === 'approved') {
      if (!approvedAmtClean) {
        toasterApi({
          type: 'error',
          status: 400,
          code: 'Missing Field',
          message: 'Enter a valid Approved $ before marking Approved.',
        });
        return;
      }
      if (!co) {
        toasterApi({
          type: 'error',
          status: 400,
          code: 'Missing Field',
          message: 'Enter a CO # before marking Approved.',
        });
        return;
      }
    }

    if (next === 'submitted') {
      const initialDate = toDateInputValue(
        draft.submitted_at ?? activeRow?.submitted_at ?? dayjs().toISOString(),
      );

      OverlayDialog.open(`markSubmittedDate_${targetId}`, {
        overlayId: `markSubmittedDate_${targetId}`,
        title: 'Mark Submitted',
        content: (
          <Flex flexDirection="column" gap={4} width="full">
            <VStack align="stretch" gap={2}>
              <Text fontWeight="bold">Submitted Date</Text>
              <ChakraInput
                id={`submitted-date-input-${targetId}`}
                type="date"
                defaultValue={initialDate}
              />
              <Text fontSize="xs" opacity={0.7}>
                Saved as a stable ISO datetime so the table/export keeps the
                same date.
              </Text>
            </VStack>

            <Flex justifyContent="flex-end" gap={2}>
              <Button
                variant="outline"
                onClick={() =>
                  OverlayDialog.close(`markSubmittedDate_${targetId}`)
                }
              >
                Cancel
              </Button>

              <Button
                onClick={async () => {
                  const el = document.getElementById(
                    `submitted-date-input-${targetId}`,
                  ) as HTMLInputElement | null;

                  const pickedDate = el?.value || '';
                  const submittedAtIso =
                    submittedDateToIso(pickedDate) ||
                    submittedDateToIso(dayjs().format('YYYY-MM-DD'));

                  const patch: any = {
                    status: 'submitted',
                    submitted_amount: draft.submitted_amount,
                    approved_amount: draft.approved_amount,
                    co_number: draft.co_number,
                    submitted_at: submittedAtIso,
                  };

                  if (/^rom\b/i.test(note)) {
                    patch.submitted_note = null;
                    setLocal((p: any) => ({
                      ...(p || {}),
                      submitted_note: '',
                      status: 'submitted',
                      submitted_at: submittedAtIso,
                    }));
                  } else {
                    patch.submitted_note = draft.submitted_note;
                    setLocal((p: any) => ({
                      ...(p || {}),
                      status: 'submitted',
                      submitted_at: submittedAtIso,
                    }));
                  }

                  queuePatch(targetId, patch);
                  OverlayDialog.close(`markSubmittedDate_${targetId}`);
                  await saveRow(targetId);
                }}
              >
                Confirm
              </Button>
            </Flex>
          </Flex>
        ),
      });

      return;
    }

    const patch: any = {
      status: next,
      submitted_amount: draft.submitted_amount,
      approved_amount: draft.approved_amount,
      co_number: draft.co_number,
    };

    if (next === 'rom') {
      const romNote = note || `rom ${dayjs().format('M/D/YY')}`;
      patch.submitted_note = romNote;
      setLocal((p: any) => ({
        ...(p || {}),
        submitted_note: romNote,
        status: 'rom',
      }));
    }

    if (next === 'approved' || next === 'rejected') {
      setLocal((p: any) => ({ ...(p || {}), status: next }));
    }

    queuePatch(targetId, patch);
    await saveRow(targetId);
  };
  const saveLabel =
    saveState === 'saving'
      ? 'Saving…'
      : saveState === 'dirty'
        ? `Unsaved (${dirtyCount})`
        : isFetchingList
          ? 'Refreshing…'
          : 'Saved';

  const accountingSummary = listData?.summary;
  const pendingTotal = Number(accountingSummary?.pending_total ?? 0);
  const approvedTotal = Number(accountingSummary?.approved_total ?? 0);

  const contractValueNum = useMemo(() => {
    const v = Number(String(contractValue || '').replace(/[$, ]/g, ''));
    return Number.isFinite(v) ? v : 0;
  }, [contractValue]);

  const revisedContractValue = useMemo(() => {
    const value = Number(accountingSummary?.revised_contract_value);
    return Number.isFinite(value) ? value : contractValueNum;
  }, [accountingSummary?.revised_contract_value, contractValueNum]);

  if (!projectId)
    return <Center>Select a project to view pending changes.</Center>;
  if (isLoadingList) return <Center>Loading…</Center>;

  const b = statusBadge(activeRow?.status);

  return (
    <Flex flexDirection="column" gapY={4} minH="0" height="full">
      {/* Top header */}
      <Card>
        <Flex flexDirection="column" gapY={3}>
          <Flex
            justifyContent="space-between"
            alignItems="start"
            flexWrap="wrap"
            gap={3}
          >
            <VStack align="start" gap={0} flex={1} minW="260px">
              <Text fontWeight="bold" fontSize="lg">
                {sheetTitle}
              </Text>
              <Text fontSize="sm" opacity={0.7}>
                {jobNo ? `Job #${jobNo}` : 'Job'}
                {jobName ? ` • ${jobName}` : ''}
                {contractor ? ` • ${contractor}` : ''}
                {contractValue
                  ? ` • Contract Value: ${fmtMoneyAmount(contractValueNum)}`
                  : ''}
                {contractValue
                  ? ` • Revised Contract Value: ${fmtMoneyAmount(revisedContractValue)}`
                  : ''}
              </Text>
            </VStack>

            <Flex gap={2} flexWrap="wrap" justifyContent="flex-end">
              <Text fontSize="sm" opacity={0.7} alignSelf="center">
                {saveLabel}
              </Text>

              <Button size="sm" variant="outline" onClick={openSheetSettings}>
                Sheet Settings
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={handleAddRow}
                disabled={saveState === 'saving'}
              >
                + Add Row
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={handleExport}
                disabled={saveState === 'saving' || isExporting}
              >
                {isExporting && <Spinner size="xs" mr={2} />}
                {isExporting ? 'Exporting…' : 'Export PDF'}
              </Button>
            </Flex>
          </Flex>

          <Flex gap={3} alignItems="end" flexWrap="wrap">
            <Flex flex={1} minW="260px">
              <AppInput
                label="Filter by CP #"
                placeholder="e.g. CP-001, CP01"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Flex>

            <VStack align="stretch" gap={1} minW="220px">
              <Text fontSize="sm" opacity={0.7}>
                Section
              </Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={section}
                  onChange={(e) => setSection(e.target.value as any)}
                >
                  <option value="pending">Pending Changes</option>

                  <option value="all">All</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </VStack>

            <Flex
              ml="auto"
              gap={3}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="flex-end"
            >
              <Badge colorPalette={b.colorPalette as any}>
                <Text textTransform="capitalize">{b.label}</Text>
              </Badge>

              <Text fontSize="sm" opacity={0.8}>
                Pending PCOs: <b>{fmtMoneyAmount(pendingTotal)}</b>
              </Text>
              <Text fontSize="sm" opacity={0.8}>
                Approved Total: <b>{fmtMoneyAmount(approvedTotal)}</b>
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Card>

      {/* Horizontal selected panel */}
      <Card>
        <Flex
          flexDirection={{ base: 'column', md: 'row' }}
          gap={3}
          align={{ base: 'stretch', md: 'center' }}
          justify="space-between"
        >
          <Flex align="center" gap={3} flexWrap="wrap">
            <Text fontWeight="bold">Selected:</Text>
            <Text fontWeight="bold" fontSize="lg">
              {activeRow?.cp_display || activeRow?.cp_number}
            </Text>
            <Badge colorPalette={b.colorPalette as any}>
              <Text textTransform="capitalize">{b.label}</Text>
            </Badge>

            <Text fontSize="sm" opacity={0.8}>
              SUBMT&apos;D:{' '}
              <b>{activeRow ? fmtSubmittedCell(activeRow) : '—'}</b>
            </Text>
          </Flex>

          <Flex
            gap={2}
            flexWrap="wrap"
            justifyContent={{ base: 'flex-start', md: 'flex-end' }}
          >
            <Button
              size="sm"
              variant="outline"
              onClick={() => markStatus('rom')}
              disabled={saveState === 'saving'}
            >
              Mark ROM
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => markStatus('submitted')}
              disabled={saveState === 'saving'}
            >
              Mark Submitted
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => markStatus('approved')}
              disabled={saveState === 'saving'}
            >
              Mark Approved
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorPalette="red"
              onClick={() => markStatus('rejected')}
              disabled={saveState === 'saving'}
            >
              Reject
            </Button>
          </Flex>
        </Flex>
      </Card>

      {/* Table */}
      <Card style={{ flex: 1, minHeight: 0 }}>
        <Flex flexDirection="column" gap={3} minH="0" height="full">
          <Flex flex="1" minH="0" flexDirection="column" gap={3}>
            <Table.ScrollArea
              borderWidth="1px"
              rounded="md"
              height="100%"
              maxH="none"
            >
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row bg="bg.subtle">
                    <Table.ColumnHeader width="90px">CP#</Table.ColumnHeader>
                    <Table.ColumnHeader minW="320px">
                      DESCRIPTION
                    </Table.ColumnHeader>
                    <Table.ColumnHeader width="160px">
                      SUBMT&apos;D
                    </Table.ColumnHeader>
                    <Table.ColumnHeader width="120px">
                      APPV&apos;D
                    </Table.ColumnHeader>
                    <Table.ColumnHeader width="120px">C.O.#</Table.ColumnHeader>
                    <Table.ColumnHeader width="120px">
                      Rejected
                    </Table.ColumnHeader>
                    <Table.ColumnHeader width="150px">
                      SUBMITTED $
                    </Table.ColumnHeader>
                    <Table.ColumnHeader width="150px">
                      PENDING $
                    </Table.ColumnHeader>
                    <Table.ColumnHeader width="150px">
                      APPV&apos;D $
                    </Table.ColumnHeader>
                    <Table.ColumnHeader width="80px">Save</Table.ColumnHeader>
                    <Table.ColumnHeader width="56px">Del</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {!listRows?.length ? (
                    <Table.Row>
                      <Table.Cell colSpan={11}>
                        <Center py={10}>
                          <Flex direction="column" align="center" gap={3}>
                            <Text opacity={0.7}>No pending changes.</Text>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleAddRow}
                            >
                              + Add Row
                            </Button>
                          </Flex>
                        </Center>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    (listRows ?? []).map((r: any) => {
                      const isActive = r.id === activeId;
                      const draft = getRowDraft(r);
                      const rowDirty = !!dirtyRef.current[r.id];

                      const highlight = rowHighlightProps(r) as any;
                      const selectedBg = {
                        base: 'gray.100',
                        _dark: 'gray.700',
                      };
                      const rowBg = isActive ? selectedBg : highlight?.bg;

                      const cellSelect = makeSelectHandler(r.id, selectRow);

                      return (
                        <Table.Row
                          key={r.id}
                          cursor="pointer"
                          {...highlight}
                          bg={rowBg}
                        >
                          <Table.Cell
                            bg={rowBg}
                            fontWeight="bold"
                            textAlign="center"
                            onClick={cellSelect}
                          >
                            {r.cp_display || r.cp_number}
                          </Table.Cell>

                          <Table.Cell
                            bg={rowBg}
                            minW="320px"
                            onClick={cellSelect}
                          >
                            {isActive ? (
                              <ChakraInput
                                size="sm"
                                value={draft.title}
                                onChange={(e) =>
                                  queuePatch(r.id, { title: e.target.value })
                                }
                              />
                            ) : (
                              <Text>{draft.title || '—'}</Text>
                            )}
                          </Table.Cell>

                          <Table.Cell bg={rowBg} onClick={cellSelect}>
                            {isActive ? (
                              dayjs(r?.submitted_at).isValid() &&
                              !(draft.submitted_note || '').trim() ? (
                                <Text>{fmtDate(r.submitted_at)}</Text>
                              ) : (
                                <ChakraInput
                                  size="sm"
                                  value={draft.submitted_note}
                                  placeholder="e.g. rom 2/5/26"
                                  onChange={(e) =>
                                    queuePatch(r.id, {
                                      submitted_note: e.target.value,
                                    })
                                  }
                                />
                              )
                            ) : (
                              <Text>{fmtSubmittedCell(r)}</Text>
                            )}
                          </Table.Cell>

                          <Table.Cell
                            bg={rowBg}
                            textAlign="center"
                            onClick={cellSelect}
                          >
                            <Text>{fmtDate(r.approved_at)}</Text>
                          </Table.Cell>

                          <Table.Cell bg={rowBg} onClick={cellSelect}>
                            {isActive ? (
                              <ChakraInput
                                size="sm"
                                value={draft.co_number}
                                placeholder="CO #"
                                onChange={(e) =>
                                  queuePatch(r.id, {
                                    co_number: e.target.value,
                                  })
                                }
                              />
                            ) : (
                              <Text>{r.co_number || '—'}</Text>
                            )}
                          </Table.Cell>

                          <Table.Cell
                            bg={rowBg}
                            textAlign="center"
                            onClick={cellSelect}
                          >
                            <Text>{fmtDate(r.rejected_at)}</Text>
                          </Table.Cell>

                          <Table.Cell bg={rowBg} onClick={cellSelect}>
                            {isActive ? (
                              <ChakraInput
                                size="sm"
                                value={String(draft.submitted_amount ?? '')}
                                placeholder="0.00"
                                onChange={(e) =>
                                  queuePatch(r.id, {
                                    submitted_amount: e.target.value,
                                  })
                                }
                              />
                            ) : (
                              <Text>{fmtMoneyAmount(r.submitted_amount)}</Text>
                            )}
                          </Table.Cell>

                          <Table.Cell bg={rowBg} onClick={cellSelect}>
                            <Text>{fmtMoneyAmount(r.pending_amount)}</Text>
                          </Table.Cell>

                          <Table.Cell bg={rowBg} onClick={cellSelect}>
                            {isActive ? (
                              <ChakraInput
                                size="sm"
                                value={String(draft.approved_amount ?? '')}
                                placeholder="0.00"
                                onChange={(e) =>
                                  queuePatch(r.id, {
                                    approved_amount: e.target.value,
                                  })
                                }
                              />
                            ) : (
                              <Text>
                                {fmtMoneyAmount(r.approved_accounting_amount)}
                              </Text>
                            )}
                          </Table.Cell>

                          <Table.Cell
                            bg={rowBg}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              size="xs"
                              variant="outline"
                              disabled={!rowDirty || saveState === 'saving'}
                              onClick={() => saveRow(r.id)}
                            >
                              {rowDirty ? 'Save' : 'Saved'}
                            </Button>
                          </Table.Cell>

                          <Table.Cell
                            bg={rowBg}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              size="xs"
                              variant="outline"
                              colorPalette="red"
                              onClick={() => confirmDeleteRow(r.id)}
                              disabled={saveState === 'saving'}
                            >
                              X
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })
                  )}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
};

export default ChangeOrdersIndex;
